import React from 'react';
import { StatusPill } from '../ui/Status';
import { formatMetricValue } from './performanceRows';

const STAGE_LABELS = {
  goals: 'Goals',
  allocations: 'Allocations',
  actuals: 'Actuals'
};

function renderValue(value, unit, formatType, emptyLabel) {
  return Number.isFinite(value)
    ? formatMetricValue(value, unit, formatType)
    : emptyLabel;
}

function SelectedKpiDetailPanel({ selectedKpiKey, selectedKpiMeta, selectedKpiSnapshots }) {
  if (!selectedKpiKey) {
    return null;
  }

  const goalsSnapshot = selectedKpiSnapshots?.goals || null;
  const allocationsSnapshot = selectedKpiSnapshots?.allocations || null;
  const actualsSnapshot = selectedKpiSnapshots?.actuals || null;
  const primarySnapshot = goalsSnapshot || allocationsSnapshot || actualsSnapshot || selectedKpiMeta || null;
  const title = primarySnapshot?.kpiLabel || selectedKpiKey;
  const subtitleParts = [primarySnapshot?.category, primarySnapshot?.goalName].filter(Boolean);

  const stageSnapshots = [
    { key: 'goals', snapshot: goalsSnapshot },
    { key: 'allocations', snapshot: allocationsSnapshot },
    { key: 'actuals', snapshot: actualsSnapshot }
  ];

  return (
    <section className="director-card director-selected-kpi-detail-panel">
      <div className="role-section-header">
        <div>
          <h2>Selected KPI Detail</h2>
          <p className="role-section-copy">
            {subtitleParts.length ? subtitleParts.join(' / ') : 'Current workflow context'}
          </p>
        </div>
        {selectedKpiMeta?.statusLabel ? (
          <div className="director-row-status">
            <StatusPill tone={selectedKpiMeta.statusTone || 'not-required'}>{selectedKpiMeta.statusLabel}</StatusPill>
            {selectedKpiMeta.statusDetail ? <div className="director-muted">{selectedKpiMeta.statusDetail}</div> : null}
          </div>
        ) : null}
      </div>

      <div className="director-selected-kpi-detail-header">
        <span className="summary-card-label">KPI</span>
        <strong>{title}</strong>
      </div>

      <div className="director-selected-kpi-detail-grid">
        {stageSnapshots.map(({ key, snapshot }) => (
          <article key={key} className="director-selected-kpi-stage-card">
            <div className="director-selected-kpi-stage-header">
              <strong>{STAGE_LABELS[key]}</strong>
              {snapshot?.statusLabel ? (
                <StatusPill tone={snapshot.statusTone || 'not-required'}>{snapshot.statusLabel}</StatusPill>
              ) : (
                <StatusPill tone="not-required">Not Loaded</StatusPill>
              )}
            </div>

            {snapshot ? (
              <dl className="director-selected-kpi-stage-list">
                <div>
                  <dt>Target</dt>
                  <dd>{renderValue(snapshot.target, snapshot.unit, snapshot.formatType, 'No target')}</dd>
                </div>

                {key === 'allocations' ? (
                  <div>
                    <dt>Allocation State</dt>
                    <dd>{snapshot.statusDetail || snapshot.statusLabel || 'Not allocated yet'}</dd>
                  </div>
                ) : (
                  <>
                    <div>
                      <dt>Actual</dt>
                      <dd>{renderValue(snapshot.actual, snapshot.unit, snapshot.formatType, 'No actual')}</dd>
                    </div>
                    <div>
                      <dt>Variance</dt>
                      <dd>{renderValue(snapshot.variance, snapshot.unit, snapshot.formatType, 'No variance')}</dd>
                    </div>
                  </>
                )}

                {snapshot.rowStateLabel ? (
                  <div>
                    <dt>Row State</dt>
                    <dd>{snapshot.rowStateLabel}</dd>
                  </div>
                ) : null}

                {snapshot.statusDetail ? (
                  <div className="director-selected-kpi-stage-detail">
                    <dt>Detail</dt>
                    <dd>{snapshot.statusDetail}</dd>
                  </div>
                ) : null}
              </dl>
            ) : (
              <p className="director-muted">
                Open {STAGE_LABELS[key]} in this same context to load the row snapshot for this KPI.
              </p>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

export default SelectedKpiDetailPanel;
